import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import Select from 'react-select';
import { addSparepartExpense } from '../../../actions/sparepart_expense';
import { getWarehouseOptions, getSparepartOptions } from '../../../actions/spare_part';
import { getUserOptions } from '../../../actions/user';
import { getTransportOptions } from '../../../actions/transport';
import { getMachineOptions } from '../../../actions/machine';
import { getExpenseTemplateOptions } from '../../../actions/expense_template';
import { usePermission } from '../../../hooks/usePermission';
import useActiveFirm from '../../../hooks/useActiveFirm';

const SparepartExpenseCreateForm = ({
    show,
    handleClose,
    addSparepartExpense,
    getWarehouseOptions,
    getSparepartOptions,
    getUserOptions,
    getTransportOptions,
    getMachineOptions,
    getExpenseTemplateOptions,
    spare_parts = [],
    warehouses = [],
    users = [],
    transports = [],
    machines = [],
    expense_templates = [],
    user
}) => {
    const { userCan } = usePermission();
    const { firmId, firmName } = useActiveFirm();

    if (!userCan('create sparepart expenses')) {
        return null;
    }

    // Check if user is director - only directors can see the "Ishlatuvchi" field
    const isDirector = user?.roles?.some(role => role.name === 'director');

    const [formData, setFormData] = useState({
        firm_id: '',
        spare_part_id: '',
        warehouse_id: '',
        transport_id: '',
        machine_id: '',
        expense_type: 'transport', // 'transport' or 'machine'
        quantity: '',
        notes: '',
        date: new Date().toISOString().split('T')[0],
        consumed_by: ''
    });

    const [errors, setErrors] = useState({});
    const [selectedSparePart, setSelectedSparePart] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [selectedConsumedBy, setSelectedConsumedBy] = useState(null);
    const [selectedTransport, setSelectedTransport] = useState(null);
    const [selectedMachine, setSelectedMachine] = useState(null);

    // Load initial data when component mounts and when firm is available
    useEffect(() => {
        if (!firmId) return; // Don't fetch if no firm is selected
        
        getUserOptions();
        getTransportOptions({ firm_id: firmId });
        getMachineOptions({ firm_id: firmId });
        getExpenseTemplateOptions({ is_for_sparepart: true, is_all: true });
        // Load warehouses for the active firm
        getWarehouseOptions({ firm_id: firmId, type: 'part' });
        // Load spare parts for the active firm
        getSparepartOptions({ firm_id: firmId });
    }, [firmId, getUserOptions, getTransportOptions, getMachineOptions, getExpenseTemplateOptions, getWarehouseOptions, getSparepartOptions]);

    // Refresh spare parts when modal opens to get latest stock values
    useEffect(() => {
        if (show && firmId) {
            getSparepartOptions({ firm_id: firmId });
            getWarehouseOptions({ firm_id: firmId, type: 'part' });
        }
    }, [show, firmId, getSparepartOptions, getWarehouseOptions]);

    // Initialize form data when modal opens or firm changes
    useEffect(() => {
        if (show && firmId) {
            setFormData(prev => ({
                ...prev,
                firm_id: firmId,
                date: new Date().toISOString().split('T')[0]
            }));
        }
    }, [show, firmId]);

    useEffect(() => {
        if (!show) {
            // Reset form when modal closes
            setFormData({
                firm_id: firmId || '',
                spare_part_id: '',
                warehouse_id: '',
                transport_id: '',
                machine_id: '',
                expense_type: 'transport',
                quantity: '',
                purpose: '',
                notes: '',
                date: new Date().toISOString().split('T')[0],
                consumed_by: ''
            });
            setSelectedSparePart(null);
            setSelectedWarehouse(null);
            setSelectedConsumedBy(null);
            setSelectedTransport(null);
            setSelectedMachine(null);
            setErrors({});
        }
    }, [show, firmId]);

    // Warehouses are already filtered by firm_id from the backend
    const warehouseOptions = (warehouses || []).map(warehouse => ({
        value: warehouse.id,
        label: warehouse.name
    }));

    // Spare parts available in the selected firm
    const sparePartOptions = spare_parts?.map(part => ({
        value: part.id,
        label: `${part.name} (Mavjud: ${part.in_stock} ${part.unit?.name || ''}) - ${part.warehouse?.name}`,
        data: part
    })) || [];

    const userOptions = users?.map(user => ({
        value: user.id,
        label: user.name
    })) || [];



    const transportOptions = Array.isArray(transports) ? transports.map(transport => ({
        value: transport.id,
        label: `${transport.name}${transport.plate ? ` - ${transport.plate}` : ''}`
    })) : [];

    const machineOptions = Array.isArray(machines) ? machines.map(machine => ({
        value: machine.id,
        label: machine.name
    })) : [];



    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };


    const handleSparePartChange = (selectedOption) => {
        setSelectedSparePart(selectedOption);
        setFormData(prev => ({
            ...prev,
            spare_part_id: selectedOption?.value || ''
        }));
        
        // Auto-select the warehouse where this spare part is located
        if (selectedOption?.data.warehouse_id) {
            const warehouseOption = warehouseOptions.find(w => w.value === selectedOption.data.warehouse_id);
            setSelectedWarehouse(warehouseOption);
            setFormData(prev => ({
                ...prev,
                warehouse_id: selectedOption.data.warehouse_id
            }));
        } else {
            setSelectedWarehouse(null);
            setFormData(prev => ({
                ...prev,
                warehouse_id: ''
            }));
        }
    };

    const handleWarehouseChange = (selectedOption) => {
        setSelectedWarehouse(selectedOption);
        setFormData(prev => ({
            ...prev,
            warehouse_id: selectedOption?.value || ''
        }));
    };

    const handleConsumedByChange = (selectedOption) => {
        setSelectedConsumedBy(selectedOption);
        setFormData(prev => ({
            ...prev,
            consumed_by: selectedOption?.value || ''
        }));
    };

    const handleExpenseTypeChange = (e) => {
        const expenseType = e.target.value;
        setFormData(prev => ({
            ...prev,
            expense_type: expenseType,
            transport_id: '',
            machine_id: ''
        }));
        setSelectedTransport(null);
        setSelectedMachine(null);
    };

    const handleTransportChange = (selectedOption) => {
        setSelectedTransport(selectedOption);
        setFormData(prev => ({
            ...prev,
            transport_id: selectedOption?.value || ''
        }));
    };

    const handleMachineChange = (selectedOption) => {
        setSelectedMachine(selectedOption);
        setFormData(prev => ({
            ...prev,
            machine_id: selectedOption?.value || ''
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!firmId || !formData.firm_id) newErrors.firm_id = 'Firma tanlash majburiy';
        if (!formData.spare_part_id) newErrors.spare_part_id = 'Ehtiyot qism tanlash majburiy';
        if (!formData.warehouse_id) newErrors.warehouse_id = 'Ombor tanlash majburiy';
        if (!formData.quantity || formData.quantity <= 0) newErrors.quantity = 'Miqdor kiritish majburiy va 0 dan katta bo\'lishi kerak';
        if (isDirector && !formData.consumed_by) newErrors.consumed_by = 'Ishlatuvchi tanlash majburiy';
        if (!formData.date) {
            newErrors.date = 'Sana tanlash majburiy';
        } else if (!isDirector) {
            // Check if date is within last 2 days for non-directors
            const selectedDate = new Date(formData.date);
            const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000);
            const today = new Date();
            
            if (selectedDate < twoDaysAgo || selectedDate > today) {
                newErrors.date = 'Faqat oxirgi 2 kun ichidagi sanalarni tanlashingiz mumkin';
            }
        }
        
        // Validate transport or machine based on expense type
        if (formData.expense_type === 'transport' && !formData.transport_id) {
            newErrors.transport_id = 'Transport tanlash majburiy';
        }
        if (formData.expense_type === 'machine' && !formData.machine_id) {
            newErrors.machine_id = 'Uskuna tanlash majburiy';
        }
        
        // Check if there's enough stock
        if (selectedSparePart && formData.quantity > 0) {
            const availableQuantity = selectedSparePart.data.in_stock;
            if (availableQuantity <= 0) {
                newErrors.quantity = 'Bu ehtiyot qism omborida mavjud emas';
            } else if (parseFloat(formData.quantity) > availableQuantity) {
                newErrors.quantity = `Omborida yetarli miqdor yo'q. Mavjud: ${availableQuantity}`;
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        // Prepare form data - if user is not director, set consumed_by to current user's ID
        const submissionData = { 
            ...formData,
            firm_id: firmId || formData.firm_id // Ensure firm_id is always from global active firm
        };
        if (!isDirector) {
            submissionData.consumed_by = user?.id || '';
        }

        const success = await addSparepartExpense(submissionData);
        if (success) {
            handleClose();
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Ehtiyot qism sarfi qo'shish</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Firma <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    value={firmName || ''}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                                />
                                <small className="text-muted">Firma global firm switcher orqali tanlangan</small>
                                {errors.firm_id && <div className="text-danger small">{errors.firm_id}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Ombor <span className="text-danger">*</span></Form.Label>
                                <Select
                                    value={selectedWarehouse}
                                    onChange={handleWarehouseChange}
                                    options={warehouseOptions}
                                    placeholder={firmId ? "Ombor tanlang..." : "Firma tanlang"}
                                    isClearable
                                    isSearchable
                                    isDisabled={!firmId}
                                />
                                {errors.warehouse_id && <div className="text-danger small">{errors.warehouse_id}</div>}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Ehtiyot qism <span className="text-danger">*</span></Form.Label>
                                <Select
                                    value={selectedSparePart}
                                    onChange={handleSparePartChange}
                                    options={sparePartOptions}
                                    placeholder={firmId ? "Ehtiyot qism tanlang..." : "Firma tanlang"}
                                    isClearable
                                    isSearchable
                                    isDisabled={!firmId}
                                />
                                {errors.spare_part_id && <div className="text-danger small">{errors.spare_part_id}</div>}
                                {!firmId && <div className="text-muted small">Ehtiyot qism tanlash uchun avval firma tanlang</div>}
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Expense Type Radio Buttons */}
                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Sarflash turi <span className="text-danger">*</span></Form.Label>
                                <div className="d-flex gap-4 mt-2">
                                    <Form.Check
                                        type="radio"
                                        id="expense-type-transport"
                                        name="expense_type"
                                        value="transport"
                                        checked={formData.expense_type === 'transport'}
                                        onChange={handleExpenseTypeChange}
                                        label="Transport uchun"
                                    />
                                    <Form.Check
                                        type="radio"
                                        id="expense-type-machine"
                                        name="expense_type"
                                        value="machine"
                                        checked={formData.expense_type === 'machine'}
                                        onChange={handleExpenseTypeChange}
                                        label="Uskuna uchun"
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Transport/Machine Selection */}
                    <Row>
                        <Col md={12}>
                            {formData.expense_type === 'transport' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Transport <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        value={selectedTransport}
                                        onChange={handleTransportChange}
                                        options={transportOptions}
                                        placeholder="Transport tanlang..."
                                        isClearable
                                        isSearchable
                                    />
                                    {errors.transport_id && <div className="text-danger small">{errors.transport_id}</div>}
                                </Form.Group>
                            )}
                            
                            {formData.expense_type === 'machine' && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Uskuna <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        value={selectedMachine}
                                        onChange={handleMachineChange}
                                        options={machineOptions}
                                        placeholder="Uskuna tanlang..."
                                        isClearable
                                        isSearchable
                                    />
                                    {errors.machine_id && <div className="text-danger small">{errors.machine_id}</div>}
                                </Form.Group>
                            )}
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Miqdor <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="number"
                                    step="1"
                                    name="quantity"
                                    value={formData.quantity}
                                    onChange={handleInputChange}
                                    placeholder="Miqdorni kiriting"
                                />
                                {errors.quantity && <div className="text-danger small">{errors.quantity}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Sana <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleInputChange}
                                    max={new Date().toISOString().split('T')[0]}
                                    min={isDirector ? undefined : new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                                />
                                {errors.date && <div className="text-danger small">{errors.date}</div>}
                                {!isDirector && (
                                    <small className="text-muted">
                                        Faqat oxirgi 2 kun ichidagi sanalarni tanlashingiz mumkin
                                    </small>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        {isDirector && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ishlatuvchi <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        value={selectedConsumedBy}
                                        onChange={handleConsumedByChange}
                                        options={userOptions}
                                        placeholder="Ishlatuvchini tanlang..."
                                        isClearable
                                        isSearchable
                                    />
                                    {errors.consumed_by && <div className="text-danger small">{errors.consumed_by}</div>}
                                </Form.Group>
                            </Col>
                        )}
                    </Row>

                    

                    <Row>
                        <Col md={12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Izohlar</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    placeholder="Qo'shimcha izohlar..."
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {selectedSparePart && (
                        <Alert variant={selectedSparePart.data.in_stock <= 0 ? "warning" : "info"}>
                            <strong>Tanlangan ehtiyot qism:</strong> {selectedSparePart.data.name}<br/>
                            <strong>Mavjud miqdor:</strong> {selectedSparePart.data.in_stock} {selectedSparePart.data.unit?.name || ''}<br/>
                            <strong>Ombor:</strong> {selectedSparePart.data.warehouse?.name}
                            {selectedSparePart.data.in_stock <= 0 && (
                                <div className="mt-2">
                                    <strong className="text-danger">⚠️ Bu ehtiyot qism omborida mavjud emas!</strong>
                                </div>
                            )}
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Bekor qilish
                    </Button>
                    <Button 
                        variant="primary" 
                        type="submit"
                        disabled={selectedSparePart && selectedSparePart.data.in_stock <= 0}
                    >
                        Saqlash
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

SparepartExpenseCreateForm.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    addSparepartExpense: PropTypes.func.isRequired,
    getWarehouseOptions: PropTypes.func.isRequired,
    getSparepartOptions: PropTypes.func.isRequired,
    getUserOptions: PropTypes.func.isRequired,
    getTransportOptions: PropTypes.func.isRequired,
    getMachineOptions: PropTypes.func.isRequired,
    getExpenseTemplateOptions: PropTypes.func.isRequired,
    spare_parts: PropTypes.array,
    warehouses: PropTypes.array,
    users: PropTypes.array,
    transports: PropTypes.array,
    machines: PropTypes.array,
    expense_templates: PropTypes.array,
    user: PropTypes.object
};

const mapStateToProps = state => ({
    spare_parts: state.spare_part.spare_part_options,
    warehouses: state.spare_part.warehouse_options,
    users: state.user.user_options,
    transports: state.transport.transport_options,
    machines: state.machine.machine_options,
    expense_templates: state.expense_template.expense_template_options,
    user: state.auth.user
});

export default connect(mapStateToProps, { 
    addSparepartExpense,
    getWarehouseOptions,
    getSparepartOptions,
    getUserOptions,
    getTransportOptions,
    getMachineOptions,
    getExpenseTemplateOptions
})(SparepartExpenseCreateForm); 